const MEASUREMENT_ID_RE = /^G-[A-Z0-9]+$/

const PRIVATE_PATH_PREFIXES = ['/admin', '/login', '/konto', '/rejestracja', '/zamowienie']

export function parseGa4MeasurementId(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const id = value.trim()
  if (!MEASUREMENT_ID_RE.test(id)) return null
  return id
}

export function getGa4MeasurementIdFromEnv(
  env: Record<string, string | undefined> = process.env as Record<string, string | undefined>,
): string | null {
  return parseGa4MeasurementId(env.GA4_MEASUREMENT_ID)
}

export function normalizeAnalyticsPath(pathname: string): string {
  const path = String(pathname || '/').split('#')[0].split('?')[0]
  const withSlash = path.startsWith('/') ? path : `/${path}`
  if (withSlash.length > 1 && withSlash.endsWith('/')) return withSlash.slice(0, -1)
  return withSlash || '/'
}

export function isPrivateAnalyticsPath(pathname: string): boolean {
  const path = normalizeAnalyticsPath(pathname).toLowerCase()
  return PRIVATE_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

export function sanitizePageLocation(origin: string, pathname: string): string {
  const base = String(origin || '').trim().replace(/\/+$/, '')
  const path = normalizeAnalyticsPath(pathname)
  if (!/^https?:\/\//i.test(base)) return path
  return `${base}${path}`
}

export function sanitizePageTitle(pathname: string): string {
  return normalizeAnalyticsPath(pathname)
}

export function sanitizePageReferrer(referrer: string, origin = 'https://divemate.pl'): string {
  const raw = String(referrer || '').trim()
  if (!raw) return ''
  let url: URL
  try {
    url = new URL(raw, origin)
  } catch {
    return ''
  }
  if (!/^https?:$/i.test(url.protocol)) return ''
  if (isPrivateAnalyticsPath(url.pathname)) return ''
  return sanitizePageLocation(url.origin, url.pathname)
}

export function buildSanitizedHitParams(origin: string, pathname: string, referrer: string) {
  return {
    page_location: sanitizePageLocation(origin, pathname),
    page_path: normalizeAnalyticsPath(pathname),
    page_title: sanitizePageTitle(pathname),
    page_referrer: sanitizePageReferrer(referrer, origin),
    send_page_view: false,
  }
}

export function isGaCookieName(name: string): boolean {
  const key = String(name || '').trim()
  return key === '_ga' || key === '_gid' || key === '_gat' || key.startsWith('_ga_') || key.startsWith('_gat_')
}

export function gaCookieNamesFromDocumentCookie(cookie: string): string[] {
  const names = new Set<string>()
  for (const part of String(cookie || '').split(';')) {
    const name = part.split('=')[0].trim()
    if (isGaCookieName(name)) names.add(name)
  }
  return [...names]
}

export function gaDisableProperty(measurementId: string): string {
  return `ga-disable-${measurementId}`
}

export function shouldTrackPublicPageView(input: {
  measurementId: string | null
  consent: unknown
  pathname: string
}): boolean {
  if (!parseGa4MeasurementId(input.measurementId)) return false
  if (!isAnalyticsConsentFromUnknown(input.consent)) return false
  if (isPrivateAnalyticsPath(input.pathname)) return false
  return true
}

function isAnalyticsConsentFromUnknown(value: unknown): boolean {
  return value === 'all'
}

export const GA4_CONSENT_DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
} as const

export const GA4_CONSENT_ANALYTICS_ONLY = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'granted',
} as const
